/**
 * Client Request ▸ detail ▸ **Service tasks** and **Schedule** (design v2.0
 * §3, §5–§7).
 *
 * A request is a container; the WORK is here. One request may include sampling,
 * delivery, pickup or another service, and the chosen CATEGORY decides the work
 * form, the routing and whether a laboratory Job ever follows.
 *
 * THE FORM IS ENTIRELY CONFIGURATION-DRIVEN. Every category-specific thing —
 * which fields exist, which are required, which appear only after another
 * answer, whether a route is needed — comes from `/service-definitions`. This
 * file contains NO per-category logic, which is what makes the §5 design test
 * true: adding a sixth category is one configuration row, zero new components.
 *
 * TYPE-CHANGE ORPHAN CLEARING (§6.1): changing the service type removes values
 * that belong only to the previous type. Left in place they would be invisible
 * on screen, still present in the record, and missed by the audit diff — which
 * only ever compares the CURRENT type's fields. The clears are STAGED and only
 * written at save (§11.3: an audit row is a claim that something happened, so
 * writing one for a change the user can still discard would be a lie).
 */
import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import { refuseSave, type MissingField } from './refuseSave';
import { navigateToPage } from '../../layouts/navigateTo';
import './clientRequest.css';
import { BusyIcon } from '../../components/loading';
import { CheckedCombo } from '../../components/CheckedCombo';
import { useNotify } from '../../components/notifications';
import { useConfirm, showDialog } from '../../components/confirm';
import { runWithAuditReason } from '../../api/client';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { UsDateTimeInput } from '../../components/UsDateTimeInput';
import {
  allFieldsOf, createServiceTask, deleteServiceTask, fetchServiceDefinitions,
  fetchRouteRuns, fetchServiceTasks, fetchTechnicians, resolveFields, resolveJobRule,
  resolveReport, routeRequired, serviceReportAction, updateServiceTask,
  TURNAROUND_FIELD,
  type RouteRow, type ServiceCategoryDef, type ServiceFieldDef, type ServiceTaskInput,
  type ServiceTaskRow,
} from './clientRequestApi';

interface Props {
  /** null until the request has been saved. A task cannot exist before its
   *  parent (§4.3) — but rather than blocking Add, the section mints the parent
   *  through `ensureRequestId` at save time. */
  requestId: string | null;
  /**
   * Saves the parent request if it is still new and returns its id, or null if
   * it is not complete enough to save (design §4.3).
   *
   * This is why Add Service works on a brand-new request: the user fills the
   * header, clicks Add Service, fills the service, and ONE save persists both —
   * instead of being told to go and save the header first.
   */
  ensureRequestId: () => Promise<string | null>;
  /** Bubbles up so the parent can refresh anything that counts tasks. */
  onChanged?: () => void;
  /**
   * Reports the task state upward so the page's wizard and its §10.3 release
   * gate describe REAL services rather than a guess. The section owns the task
   * list, so it is the only thing that can answer this honestly — the page
   * previously hardcoded "none added", which was wrong the moment one was.
   */
  onSummary?: (s: TaskSummary) => void;
  /** The parent request's permanent number, and who the work is for. Passed in
   *  rather than re-fetched: the page already holds them, and a second read
   *  could show a client the header no longer says. Used only by the report
   *  preview (§8), which names the request it belongs to. */
  requestNo?: string | null;
  clientName?: string | null;
  projectName?: string | null;
  /** The request project's site address (Project.ProjectLocation). A new
   *  service's Where starts from it, still editable — a request can cover more
   *  than one place — so the same address is not typed twice (owner, 2026-10-30;
   *  on AGE Test retyping it had already turned 123 Main St into 1234 Main St). */
  projectSite?: string | null;
  /** The request's agreed turnaround (its Priority). A new service inherits it
   *  as `tat` — the promise was made once, on the call (prototype v68). */
  priority?: string | null;
}

/** What the page needs to know about the services without owning them. */
export interface TaskSummary {
  /** Every service on the request, the unsaved one included. */
  total: number;
  /**
   * One line per service still MISSING REQUIRED DATA — saved or not.
   *
   * Being uncommitted is no longer one of these (2026-10-14). It was, back when
   * a save discarded such a service; now every Save writes it, having first
   * refused the save if it is incomplete, so "not saved yet" describes a state
   * that cannot survive the click the user is about to make. Leaving it here
   * made Save & Schedule refuse a service for the very thing it was about to
   * do. What is uncommitted is still reported — as `unsaved`, which the page
   * uses to say so rather than to block.
   */
  problems: string[];
  /** How many of them have not been committed yet. */
  unsaved: number;
}

/** What a flush of the uncommitted services did — see `ServiceTasksHandle`. */
export interface FlushResult {
  /** How many services were written. */
  saved: number;
  /**
   * One line per service that could NOT be written — in practice only a SERVER
   * refusal, since `checkPending` has already stopped the save when something
   * required is missing. Those services are untouched and still on screen.
   */
  blocked: string[];
}

/** How the page commits services the user described but did not press Add
 *  Service on (owner request 2026-10-14). */
export interface ServiceTasksHandle {
  /** True when at least one service is described but uncommitted. */
  hasPending: () => boolean;
  /**
   * What those services still need, checked BEFORE anything is written (owner
   * request 2026-10-14: "if the service need any mandatory data, display the
   * message before save"). Empty means every one of them can be saved.
   *
   * It also turns the editor's required marks on, so the dialog and the form
   * point at the same fields.
   */
  checkPending: () => MissingField[];
  /** Writes every such service against `parentId`. */
  flush: (parentId: string) => Promise<FlushResult>;
}

/** The editor's working copy of a task. Everything is a string so each control
 *  is controlled from the first render; the API layer does the conversion. */
interface Draft {
  /** Client-side identity. A never-saved service has no server id, but the row
   *  list, the editor and the "which one is open" check all need to tell two
   *  unsaved services apart. */
  key:          string;
  /** Creation order, so rows keep a stable position (and a stable NUMBER) as
   *  services move between the editor and the list. */
  seq:          number;
  id:           string | null;   // null = never saved
  serviceNo:    string | null;
  categoryCode: string;
  serviceType:  string;
  siteAddress:  string;
  scheduledDate: string;         // yyyy-MM-dd (the native date input's format)
  scheduledTime: string;         // HH:mm
  assignedToId: string;
  routeId:      string;
  routeLater:   boolean;
  routeName:    string;
  instructions: string;
  /** '' means "the category default" — never a pinned copy of it. */
  reportTemplate: string;
  /** The report's state, read back from the server. Not editable here — it
   *  moves only through the generate / approve endpoint. */
  reportVersion:     number | null;
  reportGeneratedAt: string | null;
  reportApprovedAt:  string | null;
  reportApprovedByName: string | null;
  fields:       Record<string, string | null>;
  /** Staged by a type change, written at save (§6.1). */
  clearedFields: string[];
}

const NO_ROUTE = 'No route';

/** One row of the Service task list. Saved rows and the unsaved one share a
 *  shape so the list renders from a single loop and the "N missing" rule is
 *  applied identically to both. `task` is null for the row that has never been
 *  saved — that is the whole difference. */
interface TaskRowVM {
  key:            string;
  task:           ServiceTaskRow | null;
  /** The unsaved service this row stands for, so clicking it reopens THAT one
   *  rather than starting over. Null for a saved row. */
  draft?:         Draft | null;
  isNew:          boolean;
  active:         boolean;
  categoryCode:   string;
  serviceType:    string;
  siteAddress:    string;
  scheduledDate:  string;
  scheduledTime:  string;
  assignedToId:   string;
  assignedToName: string;
  routeId:        string;
  routeLater:     boolean;
  routeName:      string;
  fields:         Record<string, string | null>;
}

/**
 * A brand-new service.
 *
 * <p><b>The turnaround is inherited</b> (prototype v68): the lab promised one on
 * the telephone, and that promise is the request's Priority. A service that
 * genuinely differs can be changed afterwards — but making the person re-pick
 * the answer they just gave, on every service of a multi-service request, is how
 * the two quietly end up disagreeing.</p>
 *
 * <p>It is seeded even for categories that do not ask for it. `tat` is only
 * PERSISTED for a category whose definition declares the field — the writer
 * ignores anything out of scope (§6.1) — so a Pickup carries no turnaround, and
 * switching a draft from Pickup to Survey still finds the inherited value
 * waiting rather than an empty box.</p>
 */
/** The request's Priority, when it is also one of the Turnaround options — the
 *  value a new service inherits. The two lists are separate Settings forms, so a
 *  priority with no matching turnaround is not inherited rather than seeding a
 *  value the service's own dropdown cannot show. */
function inheritedTurnaround(defs: ServiceCategoryDef[], priority?: string | null): string | null {
  if (!priority) return null;
  return defs.some(c => allFieldsOf(c).some(f => f.id === TURNAROUND_FIELD && f.options.includes(priority)))
    ? priority : null;
}

/**
 * Answers the lab gives the same way for every service, so the form never asks
 * them (owner, 2026-11-02): Sample collection is always decided in the field.
 *
 * The answer is still SAVED, only not shown: it decides the Job rule
 * ("Conditional · on eCOC receipt") and the server requires it. Like `tat` it is
 * seeded whatever the category — the writer keeps only fields the category
 * declares (§6.1) — and a service saved earlier keeps the answer it already has
 * (an empty one is filled). With the answer fixed, the fields that ask only when
 * it is "Planned" (How sampling is handled, Linked Sampling service) stay hidden
 * by their own conditions.
 */
const FIXED_ANSWERS: Readonly<Record<string, string>> = { samples: 'Determined in field' };

/** `fields` with every fixed answer filled where it is missing or empty. */
function withFixedAnswers(fields: Record<string, string | null>): Record<string, string | null> {
  const out = { ...fields };
  for (const [id, value] of Object.entries(FIXED_ANSWERS)) if (!out[id]) out[id] = value;
  return out;
}

function emptyDraft(categoryCode: string, seq: number, turnaround?: string | null,
                    siteAddress?: string | null): Draft {
  return {
    key: `new-${seq}`, seq,
    id: null, serviceNo: null, categoryCode, serviceType: '',
    siteAddress: siteAddress?.trim() ?? '', scheduledDate: '', scheduledTime: '',
    assignedToId: '', routeId: '', routeLater: false, routeName: NO_ROUTE,
    instructions: '', reportTemplate: '',
    reportVersion: null, reportGeneratedAt: null, reportApprovedAt: null,
    reportApprovedByName: null,
    fields: withFixedAnswers(turnaround ? { [TURNAROUND_FIELD]: turnaround } : {}), clearedFields: [],
  };
}

function toDraft(t: ServiceTaskRow): Draft {
  return {
    key: t.id, seq: 0,
    id: t.id, serviceNo: t.serviceNo,
    categoryCode: t.categoryCode ?? 'SUR',
    serviceType:  t.serviceType ?? '',
    siteAddress:  t.siteAddress ?? '',
    // The wire carries a full ISO date; the native input wants yyyy-MM-dd.
    scheduledDate: (t.scheduledDate ?? '').slice(0, 10),
    scheduledTime: (t.scheduledTime ?? '').slice(0, 5),
    assignedToId:  t.assignedToId ?? '',
    routeId:       t.routeId ?? '',
    routeLater:    !!t.routeLater,
    routeName:     t.routeName || NO_ROUTE,
    instructions:  t.instructions ?? '',
    reportTemplate: t.reportTemplate ?? '',
    reportVersion:     t.reportVersion ?? null,
    reportGeneratedAt: t.reportGeneratedAt ?? null,
    reportApprovedAt:  t.reportApprovedAt ?? null,
    reportApprovedByName: t.reportApprovedByName ?? null,
    fields:        withFixedAnswers(t.fields),
    clearedFields: [],
  };
}

const ServiceTasksSection = forwardRef<ServiceTasksHandle, Props>(function ServiceTasksSection({
  requestId, ensureRequestId, onChanged, onSummary,
  requestNo, clientName, projectName, projectSite, priority,
}: Props, ref) {
  const [defs,   setDefs]   = useState<ServiceCategoryDef[]>([]);
  const [techs,  setTechs]  = useState<{ id: string; label: string }[]>([]);
  /** Dated route RUNS (dbo.AI_Route) — what a stop actually joins: a service
   *  is attached to a run on a given day, not to a name in a list. */
  const [routeRuns, setRouteRuns] = useState<RouteRow[]>([]);
  const [tasks, setTasks] = useState<ServiceTaskRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [draft,   setDraft]   = useState<Draft | null>(null);
  const [tried,   setTried]   = useState(false);

  /* Unsaved services NOT currently in the editor. One request may need several
     services described before any of them is committed, so Add Service parks
     the one in progress here rather than refusing to open another. Exactly one
     place holds each service: it is in `drafts` or in `draft`, never both. */
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const seqRef = useRef(0);

  /** Open a service in the editor, parking whatever unsaved one is there. */
  const openDraft = useCallback((next: Draft | null) => {
    setDraft(cur => {
      // A never-saved service must not evaporate because the user opened
      // another one — park it, still visible as a row, still counted.
      if (cur && !cur.id && cur.key !== next?.key) {
        setDrafts(ds => ds.some(d => d.key === cur.key) ? ds : [...ds, cur]);
      }
      return next;
    });
    // The one being opened is now held by the editor, not the parked list.
    if (next) setDrafts(ds => ds.filter(d => d.key !== next.key));
    setTried(false);
  }, []);

  const addService = useCallback(() => {
    seqRef.current += 1;
    openDraft(emptyDraft(defs[0]?.code ?? 'SUR', seqRef.current, inheritedTurnaround(defs, priority),
                         projectSite));
    // `priority` and `projectSite` belong in the deps: without them the callback
    // closes over the values from the render that created it, so a request whose
    // turnaround or project was changed before Add Service was pressed seeded the
    // service with the OLD one.
  }, [defs, openDraft, priority, projectSite]);

  const notify = useNotify();
  const { confirm, alert, prompt } = useConfirm();

  // The switchboard + technician roster — fetched once, shared by every task.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [d, t, runs] = await Promise.all([
          fetchServiceDefinitions(), fetchTechnicians(), fetchRouteRuns(),
        ]);
        if (!alive) return;
        setDefs(d); setTechs(t); setRouteRuns(runs);
      } catch {
        if (alive) notify.warning('Could not load the service configuration.');
      }
    })();
    return () => { alive = false; };
  }, [notify]);

  // Only the latest load may set the list. On a brand-new request the page switches
  // to the minted id (one load) while the first service is still being created; that
  // earlier, empty answer must not land after the reload that follows the create.
  const loadSeq = useRef(0);
  const loadFor = useCallback(async (id: string | null) => {
    const seq = ++loadSeq.current;
    if (!id) { setTasks([]); return; }
    setLoading(true);
    try {
      const rows = await fetchServiceTasks(id);
      if (seq === loadSeq.current) setTasks(rows);
    }
    catch { notify.error('Could not load the service tasks.'); }
    finally { if (seq === loadSeq.current) setLoading(false); }
  }, [notify]);
  const load = useCallback(() => loadFor(requestId), [loadFor, requestId]);

  useEffect(() => { void load(); }, [load]);

  const catOf = useCallback(
    (code?: string | null) => defs.find(d => d.code === code), [defs]);

  const cat = catOf(draft?.categoryCode);

  /** The fields this draft currently shows — the ONE resolver (§6). */
  const fields = useMemo(
    () => resolveFields(cat, draft?.serviceType || null, draft?.fields ?? {}),
    [cat, draft?.serviceType, draft?.fields]);
  /** The ones the form ASKS — a fixed answer is saved but not shown. */
  const askedFields = useMemo(() => fields.filter(f => !(f.id in FIXED_ANSWERS)), [fields]);

  const needsRoute = useMemo(
    () => routeRequired(cat, draft?.fields ?? {}), [cat, draft?.fields]);

  /** NOBODY FROM THE LAB TRAVELS for this one — a courier carries it, or the
   *  client collects it. That is not "routing is optional", it is "there is no
   *  journey to route", and saying the first invites a dispatcher to put a
   *  FedEx consignment on a van's round (v68 `routeApplies`).
   *
   *  Read from the category's OWN rule rather than a hard-coded list of
   *  carriers: it is true exactly when the category decides routing by an
   *  answer, that answer has been given, and it came out "not routed". */
  const notRouted = useMemo(() => {
    if (!cat?.routeWhenField || needsRoute) return false;
    const answer = (draft?.fields ?? {})[cat.routeWhenField];
    return !!answer && !!answer.trim();
  }, [cat, draft?.fields, needsRoute]);

  /** The report configuration in force — the category's, unless the chosen
   *  service type overrides it (a Sample Pickup carries custody, so its receipt
   *  is a different document from the other pickups'). */
  const report = useMemo(
    () => resolveReport(cat, draft?.serviceType || null), [cat, draft?.serviceType]);

  const [reportBusy, setReportBusy] = useState(false);

  /** Where the report has got to: nothing raised · raised and waiting for a
   *  reviewer · issued. Derived, never stored — the version and the approval
   *  stamp already say it, and a second copy could disagree with them. */
  const reportState = useMemo<'none' | 'review' | 'approved'>(() => {
    if (!draft?.reportVersion) return 'none';
    if (report?.approvalRequired && !draft.reportApprovedAt) return 'review';
    return 'approved';
  }, [draft?.reportVersion, draft?.reportApprovedAt, report?.approvalRequired]);

  /**
   * View — the Service Task Report preview (prototype v46).
   *
   * <p>What it shows is the RECORD of the service performed: who was sent
   * where, when, to do what, and the state of the report itself. It is not the
   * analytical report — findings belong to the laboratory Job — and the note at
   * the foot says so, because a document that looks like a deliverable and is
   * not one is worse than no document.</p>
   *
   * <p>Rendered through the ONE shared dialog (standing rule 7): its card,
   * icon, border, label/value rows, note and orange button are all
   * `components/confirm`. Only the header block — template, version, who it is
   * for — is supplied here.</p>
   */
  const viewReport = async () => {
    if (!draft || !report) return;

    // A report exists only once a version has been raised. Nothing to preview
    // before that, and inventing a page would claim a record we do not have.
    if (!draft.reportVersion) {
      await alert({
        title: 'No report yet',
        message: 'Generate the Service Task Report first — there is no version to view.',
        tone: 'info',
      });
      return;
    }

    const when = [draft.scheduledDate ? fmtLocalDate(draft.scheduledDate) : null,
                  draft.scheduledTime || null].filter(Boolean).join(' ');
    const tech = techs.find(t => t.id === draft.assignedToId)?.label ?? null;
    const template = draft.reportTemplate || report.templates[0] || '—';

    // Only rows with an answer: a preview padded with dashes reads as missing
    // data when in fact the question was never asked of this category.
    const rows = ([
      ['Service',        draft.serviceNo],
      ['Request',        requestNo],
      ['Category',       cat?.name ?? draft.categoryCode],
      ['Service type',   draft.serviceType],
      ['Where',          draft.siteAddress],
      ['When',           when],
      ['Technician',     tech],
      ['Generated',      draft.reportGeneratedAt ? fmtLocalDateTime(draft.reportGeneratedAt) : null],
      // "not yet" rather than a blank: an empty approval line reads as an
      // omission, when the truthful answer is that nobody has signed it.
      ['Approved',       draft.reportApprovedAt
                           ? `${fmtLocalDateTime(draft.reportApprovedAt)}`
                             + (draft.reportApprovedByName ? ` · ${draft.reportApprovedByName}` : '')
                           : report.approvalRequired ? 'not yet' : 'not required'],
      ['Client-visible', report.clientVisible ? 'yes' : 'no'],
      ['Includes',       report.includes.join(', ')],
    ] as [string, string | null | undefined][])
      .filter(([, v]) => v != null && String(v).trim() !== '')
      .map(([label, value]) => ({ label, value: String(value) }));

    await showDialog({
      type:  'confirm',
      // Presents a record, so it takes the wide, left-aligned layout rather
      // than the narrow centred shape a question uses (prototype v46).
      variant: 'record',
      title: `Service Task Report · ${draft.serviceNo ?? 'unsaved'}`,
      message: (
        <div className="cr-rptv-head">
          <div className="cr-rptv-tpl">
            <b>{template}</b>
            <span className="cr-rptv-v">v{draft.reportVersion}</span>
          </div>
          {(clientName || projectName) && (
            <div className="cr-rptv-sub">
              {[clientName, projectName].filter(Boolean).join(' · ')}
            </div>
          )}
          <div className="cr-rptv-rule" />
        </div>
      ),
      rows,
      note: 'This is the record of the service performed. It does not replace the '
          + 'analytical report, which is produced from the laboratory Job.',
      actions: [{ label: 'Close', primary: true }],
    });
  };

  /** Raise or approve the report. Confirmed through the shared dialog like every
   *  other state-changing action (standing rule 7), audited server-side. */
  const doReport = async (action: 'generate' | 'approve') => {
    if (!draft || !report) return;

    /* The report's identity IS the Service ID, so it cannot be raised against a
       service that has none yet. Said as a blocking message rather than a
       disabled button: the user clicked because they wanted the report, and the
       useful answer is what to do next, not silence. (Prototype v43 genReport()
       guards the same way.) */
    if (!draft.id) {
      const stillNeeds = missingOf(draft);
      await alert({
        title: 'Save the service first',
        message: stillNeeds.length
          ? 'The Service Task Report belongs to the service’s ID, which is minted '
            + 'when the service is saved. This one is not complete yet — still needed: '
            + `${stillNeeds.map(f => f.label).join(', ')}.`
          : 'The Service Task Report belongs to the service’s ID, which is minted '
            + 'when the service is saved. Choose Add Service below, then generate the '
            + 'report.',
        tone: 'warning',
        okLabel: 'Got it',
      });
      return;
    }
    const regenerating = action === 'generate' && !!draft.reportVersion;

    /* A REISSUE asks for the reason instead of a yes/no. The client may already
       be holding the version this supersedes, so "why" is the one thing the
       audit trail has to carry — and the server refuses without it, so asking
       here is what keeps the refusal from being the user's first news of it. */
    let why: string | null = null;
    if (regenerating) {
      why = await prompt({
        title: 'Regenerate the report?',
        message: `Version ${draft.reportVersion} is superseded by a new one`
               + `${draft.reportApprovedAt ? ', and its approval is cleared' : ''}. `
               + 'Reissuing supersedes the version the client already has, so the '
               + 'audit trail records why.',
        placeholder: 'Why is this report being reissued?',
        required: true,
        confirmLabel: 'Regenerate',
      });
      if (why === null) return;
    } else {
      const ok = await confirm({
        title: action === 'approve' ? 'Approve this report?'
             :                        'Generate the Service Task Report?',
        message: action === 'approve'
          ? `It becomes the issued version${report.clientVisible
              ? ' and is released to the client.' : '.'}`
          : `Template "${draft.reportTemplate || report.templates[0]}", `
            + `generated on ${report.generatedOn.toLowerCase()}.`,
      });
      if (!ok) return;
    }

    setReportBusy(true);
    try {
      // The reason travels as X-Audit-Reason, so it lands on the audit row
      // itself rather than being re-typed into a message (§14b.2).
      const version = await runWithAuditReason(why,
        () => serviceReportAction(draft.id!, action));
      notify.success(action === 'approve'
        ? 'Report approved.'
        : `Service Task Report generated (v${version}).`);
      // Re-read rather than patching locally: the server owns the timestamps,
      // and it is what cleared the approval on a regenerate. One round trip
      // refreshes both the list and the open editor.
      if (requestId) {
        const fresh = await fetchServiceTasks(requestId);
        setTasks(fresh);
        const mine = fresh.find(t => t.id === draft.id);
        if (mine) setDraft(toDraft(mine));
      }
      onChanged?.();
    } catch (e) {
      notify.error(e instanceof Error ? `Could not ${action} the report: ${e.message}`
                                      : `Could not ${action} the report.`);
    } finally {
      setReportBusy(false);
    }
  };

  // ── Editing ──────────────────────────────────────────────────────────────
  const setD = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft(d => d ? { ...d, [k]: v } : d);

  const setField = (id: string, v: string) =>
    setDraft(d => d ? { ...d, fields: { ...d.fields, [id]: v } } : d);

  /** Changing the CATEGORY resets the type and keeps only the answers that
   *  still exist on the new category — a category change is a bigger reset than
   *  a type change, so it is not staged for audit, it simply starts over.
   *
   *  Only the category's OWN fields survive, not its per-type extras: the type
   *  is cleared along with the category, so an answer that belonged to a type
   *  has nothing left to belong to. Keeping it would leave a stale value ready
   *  to reappear the moment the user happened to pick a type that asks the same
   *  question again. */
  const changeCategory = (code: string) => {
    setDraft(d => {
      if (!d) return d;
      const keepIds = new Set((catOf(code)?.fields ?? []).map(f => f.id));
      const kept: Record<string, string | null> = {};
      for (const [k, v] of Object.entries(d.fields)) if (keepIds.has(k)) kept[k] = v;
      // The report template belongs to the old category's list — reset it so the
      // new list's default applies instead of a template that list does not offer.
      return { ...d, categoryCode: code, serviceType: '', fields: withFixedAnswers(kept), reportTemplate: '' };
    });
  };

  /**
   * Changing the TYPE (§6.1): find the answers that no longer have a field,
   * name them, let the user cancel, then STAGE the clear. Nothing is audited
   * until the save actually happens.
   */
  const changeType = async (nextType: string) => {
    if (!draft) return;
    const keep = new Set(resolveFields(cat, nextType, draft.fields).map(f => f.id));
    const orphans = allFieldsOf(cat).filter(f => !keep.has(f.id) && draft.fields[f.id]);

    if (orphans.length) {
      const ok = await confirm({
        title: 'Change service type?',
        // 'danger' rather than the default: this discards answers the user
        // already gave, which is destructive even though it is reversible by
        // cancelling. The shared dialog offers no 'warning' tone.
        tone: 'danger',
        confirmLabel: 'Change and clear',
        message:
          `${orphans.length} field${orphans.length > 1 ? 's do' : ' does'} not exist on `
          + `"${nextType}". ${orphans.length > 1 ? 'Their values' : 'Its value'} will be cleared: `
          + orphans.map(f => `${f.caption} (${draft.fields[f.id]})`).join(', ')
          + `. The change${orphans.length > 1 ? 's' : ''} will be recorded in the Audit Trail `
          + 'when the service is saved.',
      });
      if (!ok) return;
    }

    setDraft(d => {
      if (!d) return d;
      const next = { ...d.fields };
      const staged = [...d.clearedFields];
      for (const f of orphans) {
        // Keep the FIRST staging of a field: that value is the last SAVED one,
        // which is what an audit row must report as the old value.
        if (!staged.includes(f.id)) staged.push(f.id);
        delete next[f.id];
      }
      // A type can have its own template list (Mold Testing, Sample Pickup), so a
      // template chosen for the previous type may not be offered any more.
      return { ...d, serviceType: nextType, fields: next, clearedFields: staged, reportTemplate: '' };
    });
  };

  /** What a task still needs — mirrors the server (§14b.5), which stays
   *  authoritative. ONE rule, two callers: the editor's inline validation and
   *  the "N missing" count on every task row. They cannot disagree, which
   *  matters because the row is what tells the user a service they cannot
   *  currently see is incomplete. */

  const missingOf = useCallback((t: {
    categoryCode: string; serviceType: string; siteAddress: string;
    scheduledDate: string; assignedToId: string;
    fields: Record<string, string | null>;
  }): MissingField[] => {
    // The anchors point at the EDITOR's rows, so they are meaningful only for
    // the task currently open in it.  The summary below reads labels only.
    const m: MissingField[] = [];
    if (!t.serviceType)   m.push({ label: 'Service type', anchorId: 'cr-f-svc-type' });
    if (!t.siteAddress)   m.push({ label: 'Where',        anchorId: 'cr-f-svc-where' });
    if (!t.scheduledDate) m.push({ label: 'When',         anchorId: 'cr-f-svc-when' });
    /* Assigned to is deliberately NOT here (v68, owner request 2026-09-15): the
     * technician is set in Service Allocation, not on the call, so it cannot
     * block saving the service. */
    /* Route is deliberately NOT here. A route is built by dispatch, not on the
     * client call, so requiring one at intake would block release on a decision
     * the person taking the call is not making. The rule still SHOWS on the
     * field ("Required before dispatch") and is enforced before a route is
     * PUBLISHED. Prototype ALPACA-Client-Request-v43 taskNeed(); the earlier
     * v21 validated it here. */
    for (const f of resolveFields(catOf(t.categoryCode), t.serviceType || null, t.fields))
      if (f.required && !t.fields[f.id])
        m.push({ label: f.caption, anchorId: `cr-f-svc-${f.id}` });
    return m;
  }, [catOf]);

  const missing = useMemo(() => draft ? missingOf(draft) : [], [draft, missingOf]);

  /**
   * The save payload for ANY draft — the one open in the editor or one parked
   * by Add Service / flushed with the request.
   *
   * It resolves the field set and the report list FROM THE DRAFT ITSELF rather
   * than from the editor's `fields` / `report` memos: those describe whichever
   * service is open, so using them for a parked Pickup while a Survey is on
   * screen would send the Survey's questions with the Pickup's answers.
   */
  const inputOf = useCallback((d: Draft): ServiceTaskInput => {
    const c   = catOf(d.categoryCode);
    const fs  = resolveFields(c, d.serviceType || null, d.fields);
    const rep = resolveReport(c, d.serviceType || null);
    return {
      categoryCode:  d.categoryCode,
      serviceType:   d.serviceType || null,
      siteAddress:   d.siteAddress.trim() || null,
      scheduledDate: d.scheduledDate || null,
      scheduledTime: d.scheduledTime || null,
      assignedToId:  d.assignedToId || null,
      // Was hardcoded null, so the picker only ever wrote a LABEL and the
      // service was never actually linked to a route.
      routeId:       d.routeId || null,
      routeLater:    d.routeLater,
      routeName:     d.routeName === NO_ROUTE ? null : d.routeName,
      instructions:  d.instructions.trim() || null,
      // Only a template the CURRENT list offers is sent; anything else (e.g. a value
      // saved before the lists changed in Settings) saves as the list's default.
      // The server checks the same list and refuses a mismatch.
      // The default is the list's first template — the one the picker shows when
      // nothing was chosen (E2E 2026-10-18: it showed "Blank Survey Report" and saved null).
      reportTemplate: d.reportTemplate && rep?.templates.includes(d.reportTemplate)
        ? d.reportTemplate : rep?.templates[0] ?? null,
      // Only the fields currently IN SCOPE are sent — a stale answer can never
      // re-enter the record through a payload.
      // Settings-backed answers travel as NAMES too; the server stores each as its
      // row's Oid.
      fields: Object.fromEntries(fs.map(f => [f.id, d.fields[f.id] ?? null])),
      clearedFields: d.clearedFields,
    };
  }, [catOf]);

  const toInput = inputOf;

  /** Has this editor actually changed anything? Compared on the SAVE PAYLOAD,
   *  so cosmetic draft state cannot register as an edit and a real change
   *  cannot be missed. A never-saved draft always counts as changed — it does
   *  not exist yet. (v68 `taskChanged`.) */
  const draftChanged = (d: Draft): boolean => {
    const saved = d.id ? tasks.find(t => t.id === d.id) : undefined;
    if (!saved) return true;
    return JSON.stringify(inputOf(d)) !== JSON.stringify(inputOf(toDraft(saved)));
  };

  const taskChanged = (): boolean => !!draft && draftChanged(draft);

  /** Every service the user has described but not committed — the one in the
   *  editor (new, or an existing one with unsaved edits) and any parked by
   *  opening another. */
  const pendingDrafts = (): Draft[] =>
    [...(draft ? [draft] : []), ...drafts].filter(d => !d.id || draftChanged(d));

  /** How a service is named when it has no Service ID yet. "Service 2" would
   *  name a position the user cannot see from the message; the category and
   *  type are what they just typed. */
  const nameOf = (d: Draft): string =>
    d.serviceNo
    || [catOf(d.categoryCode)?.name ?? d.categoryCode, d.serviceType].filter(Boolean).join(' · ')
    || 'New service';

  /**
   * WHAT THE UNCOMMITTED SERVICES STILL NEED — checked when the REGISTRATION is
   * saved, before anything is written (owner request 2026-10-14: "if the
   * service need any mandatory data, display the message while save the
   * registration").
   *
   * The page refuses the save on a non-empty answer, so the user is told at the
   * moment they ask for the save rather than afterwards, and nothing is on disk
   * in a state the message is describing. It is the SAME `missingOf` the editor
   * and the task rows use (§14b.5 mirrors the server), so the three can never
   * name different fields.
   *
   * Anchors are attached only for the service OPEN in the editor: they point at
   * rows of that form, and a parked service's fields are not on screen to jump
   * to. Those entries carry the service's name instead, so the user still knows
   * which one to open.
   */
  const checkPending = useCallback((): MissingField[] => {
    const pending = pendingDrafts();
    // The editor shows its required marks for the service it is holding, which
    // is what the dialog is about to talk about.
    if (pending.length) setTried(true);

    const many = pending.length > 1;
    return pending.flatMap(d => {
      const open = draft?.key === d.key;
      return missingOf(d).map(m => ({
        label: many ? `${nameOf(d)} — ${m.label}` : m.label,
        ...(open && m.anchorId ? { anchorId: m.anchorId } : {}),
      }));
    });
  }, [draft, drafts, tasks, missingOf, inputOf, catOf]);

  /**
   * SAVES THE SERVICES WITH THE REQUEST (owner request 2026-10-14: "if the user
   * doesn't click the Add Service button then save those details automatically").
   *
   * Add Service stays — it commits one service on its own, with its own
   * confirmation. This is the other path: the user described a service, went
   * straight to Save Draft / Save & Close / Save & Schedule, and expects what
   * is on screen to be what is stored. Until now that work was silently lost.
   *
   * A service the SERVER would refuse (§14b.5 — Service type, Where, When and
   * the type's required answers) is NOT dropped and NOT force-written: it stays
   * exactly where it is, open or parked, and is reported back so the caller can
   * say which service needs what. Saving the request while telling the user
   * plainly that one service is still incomplete keeps both halves of the
   * screen honest; closing over it would lose the very data this exists to keep.
   *
   * No per-service confirm dialog: the caller has already confirmed the save it
   * is performing, and a second "Do you want to Save?" per service would ask the
   * same question three times for one click. Every write is audited server-side
   * exactly as Add Service's is.
   */
  const flush = useCallback(async (parentId: string): Promise<FlushResult> => {
    const pending = pendingDrafts();
    const blocked: string[] = [];
    const done   = new Set<string>();
    let   saved  = 0;

    for (const d of pending) {
      const m = missingOf(d);
      if (m.length) { blocked.push(`${nameOf(d)}: ${m.map(x => x.label).join(', ')}`); continue; }
      try {
        if (d.id) await updateServiceTask(d.id, inputOf(d));
        else      await createServiceTask(parentId, inputOf(d));
        done.add(d.key);
        saved += 1;
      } catch (e) {
        blocked.push(`${nameOf(d)}: ${e instanceof Error ? e.message : 'could not be saved'}`);
      }
    }

    // Only the committed ones leave the editor: a blocked service stays open on
    // the fields it still needs, with its * tints showing.
    if (done.size) {
      setDrafts(ds => ds.filter(d => !done.has(d.key)));
      setDraft(cur => (cur && done.has(cur.key) ? null : cur));
    }
    if (blocked.length) setTried(true);

    // Read the services BACK rather than trusting the writes: the rows now show
    // their minted Service IDs, and the summary the page reads is recomputed
    // from the stored record.
    try { setTasks(await fetchServiceTasks(parentId)); } catch { /* keep what we have */ }
    if (saved) onChanged?.();

    return { saved, blocked };
  }, [draft, drafts, tasks, missingOf, inputOf, catOf, onChanged]);

  useImperativeHandle(ref, () => ({
    hasPending: () => pendingDrafts().length > 0,
    checkPending,
    flush,
  }));

  const save = async () => {
    if (!draft) return;
    setTried(true);

    // Nothing to write is worth saying, not worth a round trip, a confirmation
    // and an audit-free "Saved successfully." that claims something happened.
    if (draft.id && !taskChanged()) {
      notify.info('No changes to save.');
      return;
    }
    if (missing.length) {
      await refuseSave(missing, { subject: 'service' });
      return;
    }
    const ok = await confirm({
      title: 'Save',
      message: 'This service and its field changes are saved and audited. Do you want to Save?',
    });
    if (!ok) return;

    setSaving(true);
    try {
      // THE PARENT IS MINTED FIRST (§4.3). On a brand-new request this saves the
      // request and returns its id, so one click persists both. It returns null
      // when the request itself is incomplete — the user has already been told
      // which field is missing, so just stop.
      const parentId = await ensureRequestId();
      if (!parentId) return;

      if (draft.id) {
        await updateServiceTask(draft.id, toInput(draft));
        notify.success('Saved successfully.');
      } else {
        const created = await createServiceTask(parentId, toInput(draft));
        notify.success(`Service ${created.serviceNo} added.`);
      }
      setDraft(null); setTried(false);
      // By the request just saved, not `load`: on a new request this handler still
      // holds requestId = null, which would clear the list it has just added to.
      await loadFor(parentId);
      onChanged?.();
    } catch (e) {
      notify.error(e instanceof Error ? `Could not save the service: ${e.message}`
                                      : 'Could not save the service.');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = async () => {
    if (!draft) return;
    // Closing an untouched editor discards nothing, so asking "discard?" would
    // invent a decision the user has not made (v68 closes silently).
    if (draft.id && !taskChanged()) { setDraft(null); setTried(false); return; }
    // A never-saved draft is DISCARDED; an existing one merely reverts. Hiding
    // the editor was never the same thing as cancelling.
    const ok = await confirm({
      title: draft.id ? 'Discard changes to this service?' : 'Discard this service?',
      tone: 'danger',
      confirmLabel: 'Discard',
      message: draft.id
        ? 'The service returns to its last saved values.'
        : 'It was never saved, so it is removed from the request.',
    });
    if (!ok) return;
    setDraft(null); setTried(false);
  };

  // Service Tasks shortcuts (CLAUDE.md standing rule 14, §18.6). This section is
  // embedded in ClientRequestDetailPage, so the editor's Save/Cancel are gated on
  // the editor being OPEN (draft != null): the store's last-registered-wins + the
  // enabled() gate then let the OPEN editor own Ctrl+S / Esc, and hand them back to
  // the parent request when the editor is closed. (Row-level report actions —
  // Generate/View/Approve/Regenerate — are per-row and stay mouse/Tab-driven.)
  useHotkeys([
    { combo: 'alt+n', label: 'Add Service', group: 'Service Tasks', order: 1, disabled: saving, run: addService },
    { combo: 'mod+s', label: 'Save Service', group: 'Service Tasks', order: 2, disabled: !draft || saving, run: () => { void save(); } },
    { combo: 'esc', label: 'Discard / Cancel edit', group: 'Service Tasks', order: 3, allowInInput: false, disabled: !draft || saving, run: () => { void cancelEdit(); } },
  ]);

  const remove = async (t: ServiceTaskRow) => {
    const ok = await confirm({
      title: 'Remove this service task?',
      tone: 'danger',
      confirmLabel: 'Remove',
      message: `${t.serviceNo ?? 'This service'} is removed from the request. `
             + 'Its Service ID is retired and never reused.',
    });
    if (!ok) return;
    try {
      await deleteServiceTask(t.id);
      notify.success('Deleted successfully.');
      if (draft?.id === t.id) setDraft(null);
      await load();
      onChanged?.();
    } catch { notify.error('Could not remove the service.'); }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  // MEMOISED deliberately: this array is a dependency of the `rows` memo,
  // which is a dependency of the summary effect.  Un-memoised, `.filter()`
  // returns a new array every render, so `rows` changed identity every render,
  // the effect re-ran, it called onSummary → the parent re-rendered → repeat.
  // React hit "Maximum update depth exceeded" and bailed out.
  const savedTasks = useMemo(() => tasks.filter(t => !t.isDraft), [tasks]);

  /** The task LIST is one row per service, saved or not — the unsaved one
   *  included, carrying an "Unsaved" pill. That is deliberate: a service being
   *  filled in is part of the request the moment it is started, and hiding it
   *  until it saves means the count, the missing-field warning and the delete
   *  affordance all arrive late. A row being EDITED shows the live draft values
   *  rather than what is on the server, so the row and the form below never
   *  disagree while the user types. (Prototype v43 pushes the draft straight
   *  into r.tasks for the same reason.) */
  const rows = useMemo<TaskRowVM[]>(() => {
    const list: TaskRowVM[] = savedTasks.map(t => {
      const live = draft?.id === t.id ? draft : null;   // being edited right now
      return {
        key:        t.id,
        task:       t,
        draft:      null,
        isNew:      false,
        active:     draft?.id === t.id,
        categoryCode:  live?.categoryCode  ?? t.categoryCode ?? 'SUR',
        serviceType:   live?.serviceType   ?? t.serviceType ?? '',
        siteAddress:   live?.siteAddress   ?? t.siteAddress ?? '',
        scheduledDate: live?.scheduledDate ?? (t.scheduledDate ?? '').slice(0, 10),
        scheduledTime: live?.scheduledTime ?? (t.scheduledTime ?? '').slice(0, 5),
        assignedToId:  live?.assignedToId  ?? t.assignedToId ?? '',
        assignedToName: live
          ? (techs.find(x => x.id === live.assignedToId)?.label ?? '')
          : (t.assignedToName ?? ''),
        routeId:    live?.routeId ?? t.routeId ?? '',
        routeLater: live?.routeLater ?? !!t.routeLater,
        routeName:  live?.routeName ?? t.routeName ?? '',
        fields:     live?.fields ?? t.fields ?? {},
      };
    });
    // Every unsaved service — the one in the editor and the parked ones — in
    // creation order, so a row keeps its number as services move in and out of
    // the editor.
    const unsaved = [...drafts, ...(draft && !draft.id ? [draft] : [])]
      .sort((a, b) => a.seq - b.seq);

    for (const d of unsaved) {
      list.push({
        key: d.key, task: null, draft: d, isNew: true,
        active: draft?.key === d.key,
        categoryCode: d.categoryCode, serviceType: d.serviceType,
        siteAddress: d.siteAddress, scheduledDate: d.scheduledDate,
        scheduledTime: d.scheduledTime, assignedToId: d.assignedToId,
        assignedToName: techs.find(x => x.id === d.assignedToId)?.label ?? '',
        routeId: d.routeId, routeLater: d.routeLater, routeName: d.routeName, fields: d.fields,
      });
    }
    return list;
  }, [savedTasks, drafts, draft, techs]);

  /** The Service Task Reports roll-up — one entry per service whose definition
   *  says it produces a report. Built from the SAME `resolveReport` the editor
   *  uses, so the table and the panel can never describe the same service
   *  differently. A service whose category produces none is simply absent:
   *  listing it as "n/a" would pad the table with rows that answer nothing. */
  const reportRows = useMemo(() => rows.flatMap(r => {
    const c   = catOf(r.categoryCode);
    const cfg = resolveReport(c, r.serviceType || null);
    if (!cfg?.required) return [];

    const t       = r.task;
    const version = t?.reportVersion ?? null;
    const state: 'pending' | 'review' | 'approved' =
      !version                                     ? 'pending'
      : cfg.approvalRequired && !t?.reportApprovedAt ? 'review'
      :                                              'approved';

    return [{
      key:           r.key,
      task:          t,
      draft:         r.draft ?? null,
      serviceNo:     t?.serviceNo ?? null,
      categoryName:  c?.name ?? r.categoryCode,
      template:      t?.reportTemplate || cfg.templates[0] || '—',
      version,
      generatedAt:   t?.reportGeneratedAt ?? null,
      clientVisible: cfg.clientVisible,
      stateLabel:    state === 'pending' ? 'Pending generation'
                   : state === 'review'  ? 'Awaiting approval'
                   :                       'Approved',
      stateTone:     state === 'pending' ? 'warn'
                   : state === 'review'  ? 'info'
                   :                       'ok',
    }];
  }), [rows, catOf]);

  /* Report upward whenever the services change, so the page's wizard and
     release gate read the real state. An UNSAVED service is a blocker in its
     own right — releasing a request whose service was never committed would
     dispatch a technician to an address nobody agreed (§10.3). */
  const lastSummary = useRef<string>('');
  useEffect(() => {
    if (!onSummary) return;
    const problems = rows.flatMap((r, i) => {
      const label = r.task?.serviceNo ?? `Service ${i + 1}`;
      const m = missingOf(r);
      return m.length ? [`${label}: ${m.map(x => x.label).join(', ')}`] : [];
    });
    const summary = { total: rows.length, problems, unsaved: rows.filter(r => r.isNew).length };
    // Push only on a REAL change.  The parent stores this in state, so sending
    // an equal-but-new object would re-render it for nothing — and any future
    // dependency that loses identity stability would spin instead of settling.
    const key = JSON.stringify(summary);
    if (key === lastSummary.current) return;
    lastSummary.current = key;
    onSummary(summary);
  }, [rows, missingOf, onSummary]);

  const fieldControl = (f: ServiceFieldDef) => {
    const v = draft?.fields[f.id] ?? '';
    const bad = tried && f.required && !v;
    const cls = `sd-input${bad ? ' required-empty' : ''}`;
    // A saved value no longer offered (its row was deleted in Settings) is still
    // shown, so the service reads as it was chosen and saves unchanged.
    const retired = v && !f.options.includes(v);
    if (f.type === 'msel') {
      // Several picks, stored joined by "; ". A pick no longer offered is kept and shown.
      const picks = v.split(';').map(x => x.trim()).filter(Boolean);
      const options = [...f.options, ...picks.filter(p => !f.options.includes(p))];
      return (
        <CheckedCombo options={options} values={picks} onChange={ns => setField(f.id, ns.join('; '))}
                      placeholder="— select —" required={f.required} ariaLabel={f.caption} unit="tests" summaryJoin="; " />
      );
    }
    return f.type === 'sel' ? (
      <select className={cls} value={v} onChange={e => setField(f.id, e.target.value)}>
        <option value="">— select —</option>
        {retired && <option value={v}>{v}</option>}
        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input className={cls} value={v} placeholder={f.placeholder ?? ''}
             onChange={e => setField(f.id, e.target.value)} />
    );
  };

  return (
    <>
      {/* ── Service tasks ─────────────────────────────────────────────── */}
      <div className="cr-card" id="sec-services">
        <div className="cr-card-h">
          <div>
            <h3>Service tasks</h3>
            <div className="cr-section-sub">
              One request may include sampling, delivery, pickup or another service.
            </div>
          </div>
          {/* Enabled even on an unsaved request: saving the service mints the
              request first (§4.3), so there is no reason to make the user save
              the header separately before they can describe the work. */}
          {/* Always available. A request often needs several services described
              before any is committed, so opening another PARKS the one in
              progress as a row rather than refusing or discarding it. */}
          <button className="ca-btn" disabled={saving}
                  title={`Add a service to this request (${formatCombo('alt+n')})`}
                  onClick={addService}>
            <span className="tb-i tb-i-blue"><i className="ti ti-plus" /></span> Add Service
          </button>
        </div>

        {loading ? (
          <div className="cr-blank">Loading…</div>
        ) : (
          <>
            {rows.map((r, i) => {
              const c    = catOf(r.categoryCode);
              const miss = missingOf(r).length;
              return (
                <div key={r.key}
                     className={`cr-task${r.active ? ' on' : ''}`}
                     onClick={() => openDraft(r.task ? toDraft(r.task) : r.draft ?? null)}>
                  <span className="cr-task-n">{i + 1}</span>

                  <div className="cr-task-c cr-task-g1">
                    <div className="cr-task-t">
                      {c?.name ?? r.categoryCode}{r.serviceType ? ` · ${r.serviceType}` : ''}
                    </div>
                    {/* What the service PRODUCES and whether a Job follows — both
                        straight from the definition, so the row explains the
                        service without the user opening it. */}
                    <div className="cr-task-s">
                      {c?.completionRecord ?? '—'}
                      {' · '}
                      {resolveJobRule(c, r.serviceType || null, r.fields) || '—'}
                    </div>
                  </div>

                  <div className="cr-task-c cr-task-g2">
                    <div className="cr-task-t">
                      {r.scheduledDate
                        ? `${fmtLocalDate(r.scheduledDate)}${r.scheduledTime ? ` · ${r.scheduledTime}` : ''}`
                        : <span className="na">Not scheduled</span>}
                    </div>
                    <div className="cr-task-s">
                      {r.siteAddress
                        ? r.siteAddress.split(',')[0]
                        : <span className="na">No address</span>}
                    </div>
                  </div>

                  <div className="cr-task-c cr-task-g3">
                    <div className="cr-task-t">
                      {r.assignedToName || <span className="na">Unassigned</span>}
                    </div>
                    <div className="cr-task-s">
                      {/* The two no-route answers are DIFFERENT answers, which
                          is why RouteLater exists — collapsing both to "No
                          route" threw away the one the user actually gave. */}
                      {r.routeName && r.routeName !== NO_ROUTE ? r.routeName
                        : r.routeLater ? 'Route built manually'
                        : 'Routed on assignment'}
                    </div>
                  </div>

                  {r.isNew && <span className="cr-chip info">Unsaved</span>}
                  {miss > 0 && <span className="cr-chip warn">{miss} missing</span>}

                  <span className="cr-task-ed">Edit</span>
                  <i className="ti ti-trash cr-task-del"
                     title="Remove this service"
                     onClick={e => {
                       e.stopPropagation();
                       if (r.task) { void remove(r.task); return; }
                       // An unsaved row has nothing to delete on the server —
                       // dropping it IS the removal, wherever it is held.
                       setDrafts(ds => ds.filter(d => d.key !== r.key));
                       if (draft?.key === r.key) { setDraft(null); setTried(false); }
                     }} />
                </div>
              );
            })}

            {rows.length === 0 && (
              <div className="cr-empty">
                <div className="cr-empty-ic"><i className="ti ti-layout-list" /></div>
                <div className="cr-empty-h">No service tasks yet</div>
                <button className="ca-btn ca-btn-solid" onClick={addService}>
                  <span className="tb-i"><i className="ti ti-plus" /></span> Add Service
                </button>
              </div>
            )}

          </>
        )}
      </div>

      {/* ── The service editor ─────────────────────────────────────────
          Its OWN card, a sibling of the list rather than a box inside it
          (prototype v43 renders taskList and taskCard as two cards). The
          list answers "what is on this request"; the editor answers "what is
          THIS one service" — nesting the second inside the first read as if
          the editor belonged to the row directly above it.

          Order down the page: Service tasks (the list) → the service being
          edited → its configuration-driven work requirements → its Service
          Task Report. Each is the previous one narrowed. */}
      {draft && (
        <div className="cr-card cr-editor" id="sec-service-editor">
          <div className="cr-editor-hd">
            <div>
              <div className="cr-editor-h">
                {draft.serviceNo ? `${draft.serviceNo} · ` : 'New service · '}
                {cat?.name ?? ''}
              </div>
            </div>
            {/* Says out loud what §5 guarantees: nothing below is
                hard-coded per category — it all comes from the
                definition. */}
            <span className="cr-badge">Configuration-driven</span>
          </div>

          {/* Category chips — the switchboard. Changing this changes the
              whole form, which is the point of §5. */}
          <div className="cr-catchips">
            {defs.map(d => (
              <button key={d.code} type="button"
                      className={`cr-catchip${draft.categoryCode === d.code ? ' on' : ''}`}
                      onClick={() => changeCategory(d.code)}>
                <i className={`ti ${d.icon}`} /> {d.name}
              </button>
            ))}
          </div>

          {/* Label BESIDE its field, full-width 50/50 — the standing-rule-12
              layout, identical to the Items detail (`sd-grid--side`). Applies to
              all five categories, because these rows are the ones every service
              shares whichever definition is selected. */}
          <div className="sd-grid sd-grid--side cr-side">
            {/* Two columns, filled row by row (owner, 2026-11-02):
                  Service category | Service type
                  Where            | When
                  Time             | Scope of Work (two lines)
                  Assigned to      | Route, when the category shows it */}
            {/* The chosen category, echoed read-only so the form states
                which definition produced the fields below it. */}
            <div className="sd-row">
              <label className="sd-label">Service category</label>
              <input className="sd-input readonly" readOnly disabled
                     value={cat?.name ?? ''} />
            </div>

            <div id="cr-f-svc-type" className="sd-row">
              <label className="sd-label">Service type<span className="req">*</span></label>
              <select className={`sd-input${tried && !draft.serviceType ? ' required-empty' : ''}`}
                      value={draft.serviceType}
                      onChange={e => changeType(e.target.value)}>
                <option value="">— select —</option>
                {(cat?.types ?? []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div id="cr-f-svc-where" className="sd-row">
              <label className="sd-label">Where<span className="req">*</span></label>
              <input className={`sd-input${tried && !draft.siteAddress ? ' required-empty' : ''}`}
                     value={draft.siteAddress} placeholder="Street, city, state"
                     onChange={e => setD('siteAddress', e.target.value)} />
            </div>

            <div id="cr-f-svc-when" className="sd-row">
              <label className="sd-label">When<span className="req">*</span></label>
              <input type="date"
                     className={`sd-input${tried && !draft.scheduledDate ? ' required-empty' : ''}`}
                     value={draft.scheduledDate}
                     onChange={e => setD('scheduledDate', e.target.value)} />
            </div>

            {/* Any time of day, typed HH:mm or picked from the clock (owner,
                2026-10-30 — replaces the lab's five fixed slots of 2026-09-09).
                UsDateTimeInput keeps it 24-hour whatever the browser locale
                (§14b.6), which a bare <input type="time"> cannot. */}
            <div id="cr-f-svc-time" className="sd-row">
              <label className="sd-label">Time</label>
              <UsDateTimeInput className="sd-input" mode="time" value={draft.scheduledTime}
                               onChange={v => setD('scheduledTime', v)} />
            </div>

            <div className="sd-row">
              {/* "Scope of Work" — the lab's own word for it (owner, 2026-11-02;
                  was "Service instructions"). */}
              <label className="sd-label">Scope of Work</label>
              <textarea className="sd-input cr-instr" rows={2} value={draft.instructions}
                        placeholder="What the technician must do on site"
                        onChange={e => setD('instructions', e.target.value)} />
            </div>

            {/* Assigned to — READ-ONLY here, as v68 has it: the technician is
                chosen in Service Allocation, not while the client is on the
                phone. The stored value is shown and sent back unchanged on save
                (toInput), so an allocation made there is never cleared here. */}
            <div id="cr-f-svc-who" className="sd-row">
              <label className="sd-label">Assigned to</label>
              <input className="sd-input readonly" readOnly disabled
                     placeholder="Unassigned"
                     value={draft.assignedToId
                       ? (techs.find(t => t.id === draft.assignedToId)?.label ?? 'Former user')
                       : ''} />
            </div>

            {/* Route — the category's rule (§7.1) is SHOWN here, not enforced
                here. The label tells the person taking the call whether a route
                will be needed, without blocking them on a dispatch decision
                they do not make; the requirement is enforced before a route is
                published.

                TWO answers only (owner ruling 2026-09-11, as v68 shows it):
                leave it to dispatch, or build the route yourself. Routes are
                built and stops added in Route Tracking, never from the intake
                form. A service already on a route keeps it — the note below
                says which — until one of these two is chosen.

                HIDDEN when the route is only Optional (owner, 2026-11-02): the
                row is shown only when the category needs a route or is not
                routed. The draft keeps its value — a new service goes to
                dispatch ("Route it for me"), and a saved one is sent back with
                the routeLater / routeId it already has. */}
            {(needsRoute || notRouted) && (
            <div className="sd-row cr-col2">
              <label className="sd-label">
                {needsRoute ? 'Route · Required before dispatch'
                 : notRouted ? 'Route · Not routed'
                 :             'Route · Optional'}
              </label>
              <select
                className="sd-input"
                value={draft.routeLater ? 'LATER' : ''}
                onChange={e => {
                  // Only a CHANGE reaches here, so a service already on a route
                  // stays on it unless the user picks the other answer.
                  const v = e.target.value;
                  setD('routeLater', v === 'LATER');
                  setD('routeId', '');
                  setD('routeName', NO_ROUTE);
                }}
              >
                {/* v68 wording. Both options now say WHO acts and WHEN:
                    "Unassigned" described a state rather than an outcome, and
                    "Create route later" never said who creates it — the two
                    answers are "the lab will" and "I will", and they should
                    read that way to the person taking the call. */}
                <option value="">Route it for me once a technician is assigned</option>
                <option value="LATER">Do not route it — I will build the route myself</option>
              </select>
              {notRouted && (
                <div className="cr-fnote">
                  <i className="ti ti-info-circle" />
                  <span>
                    Nobody from the lab travels for this service, so it is not routed.
                  </span>
                </div>
              )}
              {draft.routeId && (() => {
                const run = routeRuns.find(r => r.id === draft.routeId);
                return (
                  <div className="cr-fnote">
                    <i className="ti ti-route" />
                    <span>
                      On route <b>{run?.routeNo ?? draft.routeName}</b>
                      {run?.routeName ? ` · ${run.routeName}` : ''} — the route is
                      managed in Route Tracking.
                    </span>
                  </div>
                );
              })()}
            </div>
            )}
          </div>

          {/* The configuration-driven work form, in its own panel. Nothing
              inside is category-specific IN CODE — it is all definition
              data, and the panel is what makes that visible: the fields
              above are the same for every service, the ones below are not.
              (The Job rule is NOT repeated here — it is on this service's
              row above, resolved from these very answers.) */}
          {askedFields.length > 0 && (
            <div className="cr-nest">
              <h4>{cat?.workLabel}</h4>
              {/* The definition-driven work fields get the SAME side-label
                  layout, so a Survey's questions read like the fixed rows above
                  them — and so do Sampling's, Pickup's, Delivery's and Bottle
                  Order's, since this one grid renders all five. */}
              <div className="sd-grid sd-grid--side cr-side">
                {askedFields.map(f => (
                  <div id={`cr-f-svc-${f.id}`} className="sd-row" key={f.id}>
                    <label className="sd-label">
                      {f.caption}{f.required && <span className="req">*</span>}
                    </label>
                    {fieldControl(f)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Service Task Report (§8) ──────────────────────────────
              The record of the service PERFORMED — a different artefact
              from the laboratory's analytical report. Where samples are
              collected the two run in parallel:
                Service Task ─┬→ Service Task Report
                              └→ eCOC → Laboratory Job → Analytical Report
              Every line below is configuration; the section is rendered
              even when a category produces no report, because "not
              required for this service" is an answer and an absent block
              is not. */}
          {report && (
            <div className="cr-nest">
              <h4>
                Service Task Report
                {!report.required
                  ? <span className="cr-chip neutral">Not required</span>
                  : reportState === 'none'
                    ? <span className="cr-chip warn">Pending generation</span>
                    : reportState === 'review'
                      ? <span className="cr-chip info">Awaiting approval</span>
                      /* "Generated" and "Approved" are not the same claim: where a
                         reviewer had to sign off, saying only "Generated" understates
                         what happened. Same wording as the Review step's table. */
                      : <span className="cr-chip ok">
                          {report.approvalRequired ? 'Approved' : 'Generated'}
                        </span>}
              </h4>

              {report.required ? (
                <>
                  {/* Wrapped in the same side-label grid as the work fields so
                      the Service Task Report's one field lines up with them
                      rather than standing alone with a label above it. */}
                  <div className="sd-grid sd-grid--side cr-side cr-rtmpl-grid">
                    <div className="sd-row cr-rtmpl">
                      <label className="sd-label">Report template</label>
                      <select className="sd-input"
                              value={report.templates.includes(draft.reportTemplate)
                                ? draft.reportTemplate : report.templates[0] || ''}
                              onChange={e => setD('reportTemplate', e.target.value)}>
                        {report.templates.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* The report belongs to a SAVED service — its identity
                      IS the Service ID — so an unsaved one cannot raise
                      it yet, and the panel says exactly that rather than
                      offering a button that would fail. */}
                  {reportState === 'none' ? (
                    <div className="cr-rpt-off">
                      <i className="ti ti-file-dots" />
                      <div>
                        <div className="cr-rpt-k">Not generated yet</div>
                      </div>
                      {/* Enabled even on an unsaved service: the click then
                          EXPLAINS why it cannot run. A disabled button with a
                          tooltip only tells the user anything if they happen to
                          hover it, and a control that never responds reads as
                          broken rather than as a rule. */}
                      <button className="ca-btn ca-btn-solid cr-rpt-gen"
                              disabled={saving || reportBusy}
                              title="Raise the Service Task Report"
                              onClick={() => void doReport('generate')}>
                        {reportBusy ? <BusyIcon busy icon="ti-file-plus" />
                                    : <span className="tb-i"><i className="ti ti-file-plus" /></span>}
                        Generate
                      </button>
                    </div>
                  ) : (
                    <table className="cr-rpt-tbl">
                      <thead>
                        <tr>
                          <th>Version</th><th>Template</th><th>Generated</th>
                          <th>Reviewed by</th><th>Approved</th><th />
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><b>v{draft.reportVersion}</b></td>
                          <td>{draft.reportTemplate || report.templates[0]}</td>
                          <td>{draft.reportGeneratedAt
                                ? fmtLocalDateTime(draft.reportGeneratedAt)
                                : <span className="na">—</span>}</td>
                          <td>{draft.reportApprovedByName || <span className="na">—</span>}</td>
                          <td>{draft.reportApprovedAt
                                ? fmtLocalDateTime(draft.reportApprovedAt)
                                : <span className="na">—</span>}</td>
                          <td className="cr-rpt-acts">
                            {/* View opens the record of the service performed
                                (prototype v46) — not the analytical report,
                                which comes from the laboratory Job; the dialog
                                says so at its foot. */}
                            <button className="ca-btn"
                                    disabled={reportBusy}
                                    title="View the record of the service performed"
                                    onClick={() => void viewReport()}>
                              <span className="tb-i"><i className="ti ti-eye" /></span> View
                            </button>
                            {/* Approve only where the definition asks for it. */}
                            {reportState === 'review' && (
                              <button className="ca-btn ca-btn-solid"
                                      disabled={saving || reportBusy}
                                      onClick={() => void doReport('approve')}>
                                {reportBusy ? <BusyIcon busy icon="ti-check" />
                                            : <span className="tb-i"><i className="ti ti-check" /></span>}
                                Approve
                              </button>
                            )}
                            {/* Regenerating SUPERSEDES the version rather than
                                overwriting it, and drops the approval with it. */}
                            <button className="ca-btn"
                                    disabled={saving || reportBusy}
                                    title="Supersede this version with a new one"
                                    onClick={() => void doReport('generate')}>
                              <span className="tb-i"><i className="ti ti-refresh" /></span> Regenerate
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </>
              ) : (
                <div className="cr-rpt-off">
                  <i className="ti ti-file-off" />
                  <div>
                    <div className="cr-rpt-k">Not required for this service</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {draft.clearedFields.length > 0 && (
            <div className="cr-staged">
              <i className="ti ti-alert-triangle" />
              {draft.clearedFields.length} field
              {draft.clearedFields.length > 1 ? 's are' : ' is'} staged for clearing by the
              type change — recorded in the Audit Trail when you save.
            </div>
          )}

          {/* Discard sits directly beside the commit (prototype v43), and
              the commit is the one solid button — the destructive action
              should never be the easiest thing to hit. */}
          <div className="cr-editor-f">
            <button className="ca-btn cr-discard" onClick={cancelEdit} disabled={saving} title={`${draft.id ? 'Cancel Changes' : 'Discard Service'} (${formatCombo('esc')})`}>
              {/* On a SAVED service this abandons edits rather than discarding
                  anything, which is what v46 calls it. */}
              {draft.id ? 'Cancel Changes' : 'Discard Service'}
            </button>
            <button className="ca-btn ca-btn-solid" onClick={save} disabled={saving} title={`${draft.id ? 'Save Service Task' : 'Add Service'} (${formatCombo('mod+s')})`}>
              {saving ? <BusyIcon busy icon="ti-check" />
                      : <span className="tb-i"><i className="ti ti-check" /></span>}
              {draft.id ? 'Save Service Task' : 'Add Service'}
            </button>
          </div>
        </div>
      )}

      {/* ── Schedule ──────────────────────────────────────────────────── */}
      <div className="cr-card" id="sec-schedule">
        <div className="cr-card-h">
          <div>
            <h3>Schedule</h3>
            <div className="cr-section-sub">
              When each service is due and who is carrying it. Changes made here or in
              Service Allocation write the same audit rows.
            </div>
          </div>
          {/* The dispatch board, now that it exists. It shows every SAVED
              service across ALL requests, so it is a change of scope rather
              than a filtered view of this one — the user is leaving this
              request, and the button says "Open" because of it. */}
          <button className="ca-btn"
                  title="Open the Service Allocation board — every saved service across all requests"
                  onClick={() => navigateToPage('svcaloc')}>
            <span className="tb-i"><i className="ti ti-adjustments-horizontal" /></span> Open Service Allocation
          </button>
        </div>

        {savedTasks.length === 0 ? (
          <div className="cr-blank">No saved service to schedule yet.</div>
        ) : (
          <table className="cr-tasktbl">
            <thead>
              <tr>
                <th>Service</th><th>Category</th><th>Date</th>
                <th>Time</th><th>Assigned to</th><th>Route</th>
              </tr>
            </thead>
            <tbody>
              {savedTasks.map(t => {
                const c = catOf(t.categoryCode);
                return (
                  <tr key={t.id}>
                    <td>{t.serviceNo ?? '—'}</td>
                    <td>{c?.name ?? t.categoryCode}</td>
                    <td>{t.scheduledDate
                      ? fmtLocalDate(t.scheduledDate)
                      : <span className="na">Not set</span>}</td>
                    <td>{t.scheduledTime
                      ? t.scheduledTime.slice(0, 5)
                      : <span className="na">—</span>}</td>
                    <td>{t.assignedToName || <span className="na">Unassigned</span>}</td>
                    <td>{t.routeName || <span className="na">{NO_ROUTE}</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Service Task Reports ────────────────────────────────────────────
          The request-level roll-up: one row per service that produces a
          completion record. It exists because the report state is otherwise
          only visible by opening each service in turn — and "which of these
          have been generated, and which are client-visible" is a question about
          the REQUEST, not about any one service.

          Field records, not analytical ones: the analytical report comes from
          the laboratory Job, and one never replaces the other (§8). */}
      {reportRows.length > 0 && (
        <div className="cr-card" id="sec-reports">
          <div className="cr-card-h">
            <div>
              <h3>Service Task Reports</h3>
            </div>
          </div>

          <table className="cr-tasktbl">
            <thead>
              <tr>
                <th>Service</th><th>Category</th><th>Report</th>
                <th>Status</th><th>Generated</th><th>Client</th><th></th>
              </tr>
            </thead>
            <tbody>
              {reportRows.map(r => (
                <tr key={r.key}>
                  {/* An unsaved service has no Service ID yet — it is minted on
                      save, and the report is named after it. */}
                  <td>{r.serviceNo ?? <span className="na">— on save —</span>}</td>
                  <td>{r.categoryName}</td>
                  <td>
                    {r.template}
                    {r.version ? <span className="na"> v{r.version}</span> : null}
                  </td>
                  <td>
                    <span className={`cr-chip ${r.stateTone}`}>{r.stateLabel}</span>
                  </td>
                  <td>{r.generatedAt
                    ? fmtLocalDateTime(r.generatedAt)
                    : <span className="na">—</span>}</td>
                  <td>{r.clientVisible ? 'Visible' : <span className="na">Internal</span>}</td>
                  <td className="cr-tasktbl-act">
                    <span className="cr-open"
                          onClick={() => openDraft(r.task ? toDraft(r.task) : r.draft ?? null)}>
                      Open
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
});

export default ServiceTasksSection;
